import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  selectLoanAndLockInfoMetrics,
  selectLoanFeesCashToCloseAmount,
  selectLockAndLoanInfoSummary,
  selectTotalLoanAmount,
  selectUnderwritingExpirationDates,
} from 'store/loanApplications/selectors';

import { KeyNumberId } from '../../previewGeneration.constants';
import {
  buildKeyNumbers,
  computePreviewVersionHash,
  formatEarliestClosing,
} from '../../previewGeneration.utils';

import { resolveApr, resolveInterestRate, resolveMonthlyPayment } from './utils';

const usePreviewKeyNumbers = () => {
  const totalLoanAmount = useSelector(selectTotalLoanAmount);
  const lockSummary = useSelector(selectLockAndLoanInfoSummary);
  const metrics = useSelector(selectLoanAndLockInfoMetrics);
  const cashToClose = useSelector(selectLoanFeesCashToCloseAmount);
  const expirationDates = useSelector(selectUnderwritingExpirationDates);

  const rate = resolveInterestRate(lockSummary?.interestRate);
  const apr = resolveApr(lockSummary?.interestRate);
  const monthlyPayment = resolveMonthlyPayment(metrics);

  const earliestClosing = useMemo(
    () => formatEarliestClosing(expirationDates?.earliestClosingDate),
    [expirationDates],
  );

  const keyNumbers = useMemo(
    () =>
      buildKeyNumbers({
        [KeyNumberId.LOAN_AMOUNT]: totalLoanAmount,
        [KeyNumberId.INTEREST_RATE]: rate,
        [KeyNumberId.APR]: apr,
        [KeyNumberId.MONTHLY_PAYMENT]: monthlyPayment,
        [KeyNumberId.CASH_TO_CLOSE]: cashToClose,
        [KeyNumberId.EARLIEST_CLOSING]: earliestClosing,
      }),
    [totalLoanAmount, rate, apr, monthlyPayment, cashToClose, earliestClosing],
  );

  const versionHash = useMemo(
    () =>
      computePreviewVersionHash({
        loanAmount: totalLoanAmount,
        rate,
        apr,
        cashToClose,
      }),
    [totalLoanAmount, rate, apr, cashToClose],
  );

  return { keyNumbers, versionHash };
};

export default usePreviewKeyNumbers;
